import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Checkbox } from 'ui-components/components/checkbox';

import notificationConfigurationPropType from './notification-configuration-prop-types';
import { useAreLocalDeliveryServicesAvailableForSeller } from '../../../../../hooks/use-are-local-delivery-services-available-for-seller';

import SpecificTimeCheckbox from './specific-time';
import NumberOfHoursCheckbox from './number-of-hours';
import messages from '../messages';
import style from '../style.module.less';

const ShipmentNotificationConfiguration = ({ onConfigUpdate, trackableConfig, untrackableConfig }) => {
  const sellerHasLocalDeliveryServices = useAreLocalDeliveryServicesAvailableForSeller();
  const updateTrackableShipmentNotificationConfiguration = useCallback(
    (value, path, clearConfig) => {
      const updatedConfig = {
        ...(clearConfig ? {} : trackableConfig),
        [path]: value,
      };
      onConfigUpdate('trackableShipmentNotificationConfiguration', updatedConfig);
    },
    [onConfigUpdate, trackableConfig],
  );
  const updateUntrackableShipmentNotificationConfiguration = useCallback(
    (value, path) => {
      const updatedConfig = {
        ...untrackableConfig,
        [path]: value,
      };
      onConfigUpdate('untrackableShipmentNotificationConfiguration', updatedConfig);
    },
    [onConfigUpdate, untrackableConfig],
  );

  //  The trackOnLabel property controls the radio buttons and should be set to "Shipping label is created" by default, however it's value comes in falsy iniitally from the DB.
  //  This logic is attempting to mirror the logic in V2 by forcing the trackOnLabel to be true initally.
  //  The V2 Logic is here: https://github.com/shipstation/throatpunch/blob/master/public%2Fviews%2FSettings%2FStores%2FDetails%2Findex.js#L195

  const isStoreInDefaultState =
    !trackableConfig.trackOnLabel &&
    !trackableConfig.trackOnMailStream &&
    !trackableConfig.trackOnTimeStampString &&
    !trackableConfig.trackTimeElapsed;

  const defaultTrackOnLabel = trackableConfig.trackOnLabel === null;
  const defaultUntrackableOnLabel = untrackableConfig.trackOnLabel === null;
  const untrackableOnLabel = defaultUntrackableOnLabel || untrackableConfig.trackOnLabel;
  const trackableOnLabel = defaultTrackOnLabel || trackableConfig.trackOnLabel;

  useEffect(() => {
    if (isStoreInDefaultState) {
      updateTrackableShipmentNotificationConfiguration(true, 'trackOnLabel', true);
      updateUntrackableShipmentNotificationConfiguration(false, 'trackOnLabel');
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={style['shipment-notification-configuration']}>
      <div className={style['input-label']}>
        <FormattedMessage {...messages.notificationTiming} />
      </div>
      <div className={style['input-element']}>
        <RadioCheckListItem
          className={style['radio-checklist']}
          isChecked={trackableOnLabel}
          onChecked={() => {
            updateTrackableShipmentNotificationConfiguration(true, 'trackOnLabel', true);
            updateUntrackableShipmentNotificationConfiguration(false, 'trackOnLabel', false);
          }}
        >
          <FormattedMessage {...messages.shippingLabelOption} />
        </RadioCheckListItem>
        <RadioCheckListItem
          className={style['radio-checklist']}
          isChecked={!trackableOnLabel}
          onChecked={() => updateTrackableShipmentNotificationConfiguration(false, 'trackOnLabel', true)}
        >
          <FormattedMessage {...messages.firstOfConditions} />
        </RadioCheckListItem>
        <div className={style.conditions}>
          <Checkbox
            checked={!!trackableConfig.trackOnMailStream}
            className={style.checkbox}
            disabled={trackableOnLabel}
            label={<FormattedMessage {...messages.hitsMailStream} />}
            onChange={(checked) => {
              updateTrackableShipmentNotificationConfiguration(checked, 'trackOnMailStream');
              updateUntrackableShipmentNotificationConfiguration(!trackableOnLabel, 'trackOnLabel', true);
            }}
          />
          <SpecificTimeCheckbox
            config={trackableConfig}
            disabled={trackableOnLabel}
            updateConfig={updateTrackableShipmentNotificationConfiguration}
          />
          <NumberOfHoursCheckbox
            config={trackableConfig}
            disabled={trackableOnLabel}
            updateConfig={updateTrackableShipmentNotificationConfiguration}
          />
          {trackableConfig.trackOnMailStream && !trackableConfig.trackOnLabel && (
            <div className={style.conditions}>
              <h5 className={style['untrackable-header']}>
                <FormattedMessage {...messages.untrackableOptionsLabel} />
              </h5>
              <RadioCheckListItem
                className={style['radio-checklist']}
                isChecked={untrackableOnLabel}
                onChecked={() => updateUntrackableShipmentNotificationConfiguration(true, 'trackOnLabel')}
              >
                <FormattedMessage {...messages.shippingLabelOption} />
              </RadioCheckListItem>
              <RadioCheckListItem
                className={style['radio-checklist']}
                isChecked={untrackableConfig.trackOnLabel === null ? false : !untrackableConfig.trackOnLabel}
                onChecked={() => updateUntrackableShipmentNotificationConfiguration(false, 'trackOnLabel')}
              >
                <FormattedMessage {...messages.firstOfConditions} />
              </RadioCheckListItem>
              <div className={style.conditions}>
                <SpecificTimeCheckbox
                  config={untrackableConfig}
                  disabled={untrackableOnLabel}
                  updateConfig={updateUntrackableShipmentNotificationConfiguration}
                />
                <NumberOfHoursCheckbox
                  config={untrackableConfig}
                  disabled={untrackableOnLabel}
                  updateConfig={updateUntrackableShipmentNotificationConfiguration}
                />
              </div>
            </div>
          )}
        </div>
        {sellerHasLocalDeliveryServices && (
          <div className={style['setting-description']}>
            <FormattedMessage {...messages.localDeliveryDisclaimer} />
          </div>
        )}
      </div>
    </div>
  );
};

ShipmentNotificationConfiguration.propTypes = {
  trackableConfig: notificationConfigurationPropType.isRequired,
  untrackableConfig: notificationConfigurationPropType.isRequired,
  onConfigUpdate: PropTypes.func.isRequired,
};

export default ShipmentNotificationConfiguration;
